import { Box, Button, IconButton, Stack, Tooltip } from '@mui/material';
import { makeStyles } from '@mui/styles';
import {
  GridColDef,
  gridDetailPanelExpandedRowsContentCacheSelector,
  GridRenderCellParams,
  GridRowParams,
  GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
  useGridApiContext,
  useGridSelector
} from '@mui/x-data-grid-pro';
import { IconArchive } from '@tabler/icons';
import { format, isValid } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import enLocale from 'date-fns/locale/en-US';
import frLocale from 'date-fns/locale/fr-CA';
import useAuth from 'hooks/useAuth';
import useProvider from 'hooks/useProvider';
import useTimezone from 'hooks/useTimezone';
import useUser from 'hooks/useUser';
import { useModal } from 'mui-modal-provider';
import { useSnackbar } from 'notistack';
import React, { useCallback, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useMutation } from 'react-query';
import { getErrorMessage } from 'utils/stringUtils';
import BasicTable from 'views/components/BasicTable';
import SubCard from 'views/components/cards/SubCard';
import FormBuilderScoreVisualization from 'views/components/form-builder/FormBuilderScoreVisualization';
import Preview from 'views/components/form-builder/Preview';
import AttachFormBasedFormModal from './AttachScoreBasedFormModal';
import DocumentAssignedFormsClientResponses from './DocumentAssignedFormsClientResponses';
import RefillModal from './RefillModal';
import useAssignedForms from './useAssignedForms';

const useStyles = makeStyles(() => ({
  Incomplete: {
    backgroundColor: '#f2dede',
    '&:hover': {
      backgroundColor: '#ebcccc !important'
    }
  },
  'assigned-to-client': {
    backgroundColor: '#f2dede',
    '&:hover': {
      backgroundColor: '#ebcccc !important'
    }
  },
  'notified-client': {
    backgroundColor: '#f2dede',
    '&:hover': {
      backgroundColor: '#ebcccc !important'
    }
  },
  Completed: {
    backgroundColor: 'inherit'
  }
}));

interface Props {
  userId: string;
}

interface ClientFormRow {
  id: string;
  fileName: string;
  lastUpdate: string;
  status: string;
  assignedFormData?: any;
  isForm?: boolean;
  index?: number;
}

export default function MeasurableForms({ userId }: Props) {
  const intl = useIntl();
  const { request, userId: therapistId } = useAuth();
  // deconstruct & get Intake Form Setting
  const { user } = useUser(userId);
  const { isClient, isAdministrator, isNousSupport, isProviderAdmin, isProvider, user: loginUser } = useUser(therapistId);
  const { provider } = useProvider(userId);
  const { data, refetch } = useAssignedForms({ status: 'assigned-to-client', patientId: userId });

  const alreadyAssignedForms = useMemo(
    () =>
      data?.assignedForms?.filter((assignedForm) => assignedForm?.form !== '')?.map((alreadyAssignedForm) => alreadyAssignedForm.form._id),
    [data?.assignedForms]
  );
  const alreadyAssignedTemplates = useMemo(
    () =>
      data?.assignedForms
        ?.filter((assignedForm) => assignedForm?.template !== '')
        ?.map((assignedTemplate) => assignedTemplate.template._id),
    [data?.assignedForms]
  );

  const { showModal } = useModal();
  const { enqueueSnackbar } = useSnackbar();
  const classes = useStyles();
  const { timezone } = useTimezone();
  function CustomDetailPanelToggle(props: Pick<GridRenderCellParams, 'id' | 'value'>) {
    const { id, value: isExpanded } = props;
    const apiRef = useGridApiContext();

    // To avoid calling ´getDetailPanelContent` all the time, the following selector
    // gives an object with the detail panel content for each row id.
    const contentCache = useGridSelector(apiRef, gridDetailPanelExpandedRowsContentCacheSelector);

    // If the value is not a valid React element, it means that the row has no detail panel.
    const hasDetail = React.isValidElement(contentCache[id]);
    if ((id as string).includes('assigned-form')) {
      return (
        <Tooltip title={<FormattedMessage defaultMessage="All Assigned Forms" />}>
          <IconButton style={{ color: isExpanded ? '#000' : '#555', backgroundColor: isExpanded ? '#e5e5e5' : '#fff', marginLeft: 10 }}>
            <IconArchive stroke={2} size={28} />
          </IconButton>
        </Tooltip>
      );
    }

    return null;
  }
  const assignedFormStatusMap = useMemo(
    () =>
      new Map([
        ['assigned-to-client', intl.formatMessage({ defaultMessage: 'Assigned to client' })],
        ['notified-client', intl.formatMessage({ defaultMessage: 'Notified client' })],
        ['filled-by-client', intl.formatMessage({ defaultMessage: 'Filled by client' })],
        ['notified-provider', intl.formatMessage({ defaultMessage: 'Notified Provider' })]
      ]),
    [intl]
  );
  const columns: GridColDef[] = [
    {
      field: 'fileName',
      headerName: intl.formatMessage({ defaultMessage: 'File Name' }),
      minWidth: 160,
      renderCell: (params) => {
        const row = params.row as ClientFormRow;
        return row.fileName;
      }
    },
    {
      field: 'lastUpdate',
      headerName: intl.formatMessage({ defaultMessage: 'Last Update' }),
      width: 300,
      renderCell: (params) => {
        const row = params.row as ClientFormRow;
        const lastUpdateMoment = new Date(row.lastUpdate);
        console.log('checkeing if valid', isValid(lastUpdateMoment), lastUpdateMoment, timezone, intl.locale, frLocale, enLocale);
        console.log(
          'format',
          format(utcToZonedTime(lastUpdateMoment, timezone), intl.locale === 'fr' ? 'do MMMM yyyy, HH:mm' : 'MMMM do yyyy, h:mm a', {
            locale: intl.locale === 'fr' ? frLocale : enLocale
          })
        );
        console.log(
          isValid(lastUpdateMoment)
            ? format(utcToZonedTime(lastUpdateMoment, timezone), intl.locale === 'fr' ? 'do MMMM yyyy, HH:mm' : 'MMMM do yyyy, h:mm a', {
                locale: intl.locale === 'fr' ? frLocale : enLocale
              })
            : '-'
        );
        //
        return isValid(lastUpdateMoment)
          ? format(utcToZonedTime(lastUpdateMoment, timezone), intl.locale === 'fr' ? 'do MMMM yyyy, HH:mm' : 'MMMM do yyyy, h:mm a', {
              locale: intl.locale === 'fr' ? frLocale : enLocale
            })
          : '-';
      }
    },
    {
      field: 'status',
      headerName: intl.formatMessage({ defaultMessage: 'Status' }),
      minWidth: 150,
      renderCell: (params) => {
        const row = params.row as ClientFormRow;
        return <strong>{params.row.id.includes('assigned-form') ? assignedFormStatusMap.get(row.status) : row.status}</strong>;
      }
    },
    {
      field: '',
      width: 340,
      renderCell: (params) => {
        const row = params.row as ClientFormRow;

        // if (row.id === 'intake-form' && intakeForm) {
        //   return (
        //     <Stack direction="row" spacing={2}>
        //       {sendEmailLoading && <Loader />}
        //       {/* {!isAdministrator && !isNousSupport && intakeForm.isCompleted && (
        //         <Button
        //           variant="contained"
        //           onClick={() => {
        //             if (!user || !intakeForm) return;
        //             const modal = showModal(IntakeFormSummaryDialog, { user, intakeForm, onClose: () => modal.hide() });
        //           }}
        //         >
        //           <FormattedMessage defaultMessage="Quick View" />
        //         </Button>
        //       )} */}

        //       {(!isAdministrator || isProvider) && !isNousSupport && intakeForm.isCompleted && user && intakeForm && (
        //         <LinkViewButton
        //           postLinkUrl={`user/${user?.id}/files/generate-pdf`}
        //           generatePostData={() => ({
        //             data: renderToString(
        //               <IntlProvider locale="en">
        //                 <IntakeFormSummary user={user} intakeForm={intakeForm} />
        //               </IntlProvider>
        //             ),
        //             fileType: 'INTAKE_FORM',
        //             lastUpdate: intakeForm.updatedAt
        //           })}
        //           keyId={intakeForm.updatedAt}
        //         />
        //       )}

        //       {(!isAdministrator || !isProvider) && !isNousSupport && (
        //         <Button color="error" variant="contained" sx={{ whiteSpace: 'nowrap' }} onClick={handleCompleteForm}>
        //           {isProviderAdmin || intakeForm.isCompleted ? (
        //             <FormattedMessage defaultMessage="Edit" />
        //           ) : (
        //             <FormattedMessage defaultMessage="Complete Form" />
        //           )}
        //         </Button>
        //       )}

        //       {!isClient && !intakeForm.isCompleted && (
        //         <Button
        //           color="primary"
        //           variant="outlined"
        //           sx={{ whiteSpace: 'nowrap' }}
        //           disabled={sendEmailLoading}
        //           onClick={() => sendEmail(intakeForm.isCompleted)}
        //         >
        //           <FormattedMessage defaultMessage="Send Reminder Email" />
        //         </Button>
        //       )}
        //     </Stack>
        //   );
        // }

        // if (row.id === 'informed-consent' && user) {
        //   return lastAcceptedInformedConsent ? (
        //     <>
        //       <LinkViewButton getLinkUrl={lastAcceptedInformedConsent.url} />
        //     </>
        //   ) : (
        //     '-'
        //   );
        // }

        if (row.id.includes('assigned-form') && user) {
          if ((!isAdministrator || isProvider) && !isNousSupport) {
            return (
              <Stack direction="row" gap={1}>
                {isClient && (row.status === 'assigned-to-client' || row.status === 'notified-client') && (
                  <Button
                    color="error"
                    variant="contained"
                    sx={{ whiteSpace: 'nowrap' }}
                    onClick={() => {
                      handleFormBuilderForm(
                        row,
                        row?.id,
                        Object.entries(row.assignedFormData?.form)?.length ? row.assignedFormData?.form : row.assignedFormData?.template,
                        row?.assignedFormData?.clientResponses,
                        row?.assignedFormData
                      );
                    }}
                  >
                    <FormattedMessage defaultMessage="Complete Form" />
                  </Button>
                )}
                {(isProvider || isProviderAdmin) && (
                  <Button onClick={() => handleSetRefillFormInterval(row.id.split('assigned-form-')[1])} color="info" variant="contained">
                    {row?.assignedFormData?.allowRefill ? (
                      <FormattedMessage defaultMessage="Update refill time" />
                    ) : (
                      <FormattedMessage defaultMessage="Set refill time" />
                    )}
                  </Button>
                )}
                {(row.assignedFormData?.clientResponses?.length > 1 || ['filled-by-client', 'notified-provider'].includes(row.status)) && (
                  <Button
                    color="info"
                    variant="contained"
                    sx={{ whiteSpace: 'nowrap' }}
                    onClick={() =>
                      handleScoreVisulization(
                        row.assignedFormData._id,
                        row.assignedFormData?.form ? row.assignedFormData?.form?.name : row.assignedFormData?.template?.name
                      )
                    }
                  >
                    <FormattedMessage defaultMessage="View Scores" />
                  </Button>
                )}
              </Stack>
            );
          }
        }

        return null;
      }
    },
    {
      ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
      width: 100,
      renderCell: (params) => <CustomDetailPanelToggle id={params.id} value={params.value} />
    }
  ];

  const sendEmailMutation = useMutation(
    (isCompleted: boolean) =>
      request.post(`therapists/${therapistId}/patients/${userId}/intake-form/send-email?isCompleted=${Number(isCompleted)}`),
    {
      onSuccess(response) {
        enqueueSnackbar(
          intl.formatMessage({ defaultMessage: 'Email has been sent to {email} successfully!' }, { email: response.data.email }),
          { variant: 'success' }
        );
      },
      onError(error) {
        enqueueSnackbar(getErrorMessage(error), { variant: 'error' });
      }
    }
  );
  const { mutate: sendEmail, isLoading: sendEmailLoading } = sendEmailMutation;
  const handleSetRefillFormInterval = useCallback(
    (assignId) => {
      const modal = showModal(RefillModal, {
        onClose: () => {
          refetch();
          modal.hide();
        },
        assignId
      });
    },
    [showModal, refetch]
  );

  // const handleCompleteForm = useCallback(() => {
  //   if (!intakeForm || !user) {
  //     return;
  //   }

  //   const modal = showModal(IntakeFormDialog, {
  //     userId,
  //     onClose: () => modal.hide()
  //   });
  // }, [user, intakeForm, showModal, userId]);

  const handleFormBuilderForm = useCallback(
    (form, rowId, row, clientResponses, assignedForm) => {
      console.log('form here is ', form);
      console.log('row', row, assignedForm, 'assigned form', clientResponses, 'client responses');
      const clientHistoryLength = clientResponses?.length;
      const isForm = Object.entries(assignedForm?.form).length ? true : false;
      console.log(assignedForm, 'assigned formdddd');
      const initialValues = isForm
        ? row.type === 'default'
          ? clientResponses[clientHistoryLength - 1]?.form?.items
          : clientResponses[clientHistoryLength - 1]?.form?.steps
        : row.type === 'default'
        ? clientResponses[clientHistoryLength - 1]?.template?.items
        : clientResponses[clientHistoryLength - 1]?.template?.steps;
      return showModal(Preview, {
        type: row.type,
        steps: row.type === 'default' ? row.items : row.steps,
        defaultTypeTitle:
          row.type === 'default' && user?.preferredLanguage === 'french'
            ? row.steps[0].titleFrench || row.steps[0].title || row.name
            : row.steps[0].title || row.name,
        collectScores: true,
        isForm,
        assignId: rowId.split('assigned-form-')[1],
        initialValues: ['filled-by-client', 'notified-provider'].includes(row?.status) ? initialValues : {},
        onClose: () => refetch(),
        disableFormInputs: false,
        preferredLanguage: user?.preferredLanguage,
        preventInitialApiCall: true,
        source: 'MeasurableForms'
      });
    },
    [showModal, refetch, user?.preferredLanguage]
  );
  const handleAttachScoreBasedForm = () => {
    const modal = showModal(AttachFormBasedFormModal, {
      userId,
      onClose: () => {
        refetch();
        modal.hide();
      },
      alreadyAssignedForms,
      alreadyAssignedTemplates,
      therapistId: therapistId ? therapistId : ''
    });
  };
  const handleScoreVisulization = useCallback(
    (assignId: string, formName: string) => {
      const modal = showModal(FormBuilderScoreVisualization, {
        onClose: () => {
          // refetch();
          modal.hide();
        },
        assignId,
        userId,
        formName
      });
    },
    // [showModal, userId, refetch]
    [showModal, userId]
  );
  const rows = useMemo(() => {
    if (!user || !data) {
      return null;
    }

    // const intakeFormInfo: ClientFormRow = {
    //   id: 'intake-form',
    //   fileName: 'Intake Form',
    //   lastUpdate: intakeForm.updatedAt && intakeForm.isCompleted ? intakeForm.updatedAt : '-',
    //   status: intakeForm.isCompleted ? 'Completed' : 'Incomplete'
    // };
    // only add the intake if enabled

    const result: ClientFormRow[] = [];

    // if (INFORMED_CONSENT && (isClient || hasInformedConsent) && lastAcceptedInformedConsent) {
    //   result.push({
    //     id: 'informed-consent',
    //     fileName: lastAcceptedInformedConsent.fileName,
    //     lastUpdate: lastAcceptedInformedConsent.lastUpdate,
    //     status: lastAcceptedInformedConsent.status
    //   });
    // }

    if (data) {
      data?.assignedForms?.forEach((ele, index) => {
        return result.push({
          index,
          id: `assigned-form-${ele._id}`,
          fileName: ele?.form?.name || ele?.template?.name,
          lastUpdate: ele?.updatedAt,
          status: ele?.status,
          assignedFormData: ele,
          isForm: Object.entries(ele.form).length ? true : false
        });
      });
    }
    return result;
  }, [user, data]);
  return (
    <SubCard
      title={<FormattedMessage defaultMessage="Measurable Forms" />}
      secondary={
        isProvider ? (
          <Button variant="contained" onClick={handleAttachScoreBasedForm}>
            <FormattedMessage defaultMessage="Assign Forms and Templates" />
          </Button>
        ) : null
      }
    >
      <BasicTable
        emptyMessage={<FormattedMessage defaultMessage="No documents found." />}
        columns={columns}
        hideSearch
        rows={rows?.filter((form) => form?.assignedFormData?.isMeasurableForm)}
        getRowClassName={(params: GridRowParams) => classes[params?.row?.status]}
        getDetailPanelContent={({ row }) => {
          // if (row.id === 'informed-consent' && informedConsents?.length >= 1)
          //   return (
          //     <Box p={2}>
          //       <DocumentInformedConsent userId={userId} informedConsents={informedConsents} />
          //     </Box>
          //   );
          if ((row.id as string).includes('assigned-form')) {
            return (
              <Box>
                <DocumentAssignedFormsClientResponses
                  user={user}
                  clientResponses={data?.assignedForms[row?.index]?.clientResponses}
                  index={row.index}
                  isForm={row?.isForm}
                  row={row}
                />
              </Box>
            );
          }
          return null;
        }}
        getDetailPanelHeight={({ row }) => 'auto'}
      />
    </SubCard>
  );
}
